import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslator } from 'tradera/lang/translate';
import { Dialog, DialogPortal, DialogOverlay } from '@radix-ui/react-dialog';
import BasicCarousel from 'tradera/components/carousels/basic/basic';
import { BREAKPOINT_SIZES, BREAKPOINTS } from 'tradera/constants/breakpoints';
import CloseIcon from '@tradera/blueprint/theme/icons2/close.svg?jsx';
import { ViewItemVideo } from 'tradera/components/carousels/view-item-carousel-zoom/view-item-video';
import { Button } from 'tradera/components/atoms/button/button';
import dialogModalStyles from 'tradera/components/dialog/dialog-modal.module.scss';
import { classNames } from 'tradera/utils/class-names';

const ImageGalleryModal = (props) => {
    const { images, initialSlideIndex, closeModalCallback, isVideoEnabled } =
        props;

    const { t } = useTranslator();
    const carouselRef = useRef(null);
    const closeButtonRef = useRef(null);

    const environment = useSelector((state) => state.environment);

    const { isMobileDevice } = environment;

    const [activeSlideIndex, setActiveSlideIndex] = useState(initialSlideIndex);

    const handleCloseModalClick = (event) => {
        event.stopPropagation();
        closeModalCallback();
    };

    const stopClickEventPropagationFromCarousel = (event) =>
        event.stopPropagation();

    const stopClickEventsFromImage = (event) => event.stopPropagation();

    const navigate = useCallback((e) => {
        if (e.keyCode === 37) {
            // left
            carouselRef.current.previous();
            e.preventDefault();
        } else if (e.keyCode === 39) {
            // right
            carouselRef.current.next();
            e.preventDefault();
        }
    }, []);

    useEffect(() => {
        closeButtonRef.current.focus();
        window.addEventListener('keydown', navigate);
        return () => {
            window.removeEventListener('keydown', navigate);
        };
    }, [navigate]);

    const settings = {
        initialSlide: initialSlideIndex,
        adaptiveHeight: true,
        lazyLoad: 'progressive',
        fade: !isMobileDevice,
        useCss: false,
        useTransform: false,
        responsive: [
            {
                breakpoint: BREAKPOINT_SIZES[BREAKPOINTS.MD].min,
                settings: {
                    arrows: false
                }
            }
        ],
        afterChange: (index) => {
            setActiveSlideIndex(index);
        }
    };

    return (
        <Dialog
            defaultOpen
            onOpenChange={(open) => {
                if (!open) {
                    closeModalCallback();
                }
            }}>
            <DialogPortal forceMount>
                <DialogOverlay
                    className={classNames(
                        dialogModalStyles.overlay,
                        'image-gallery-modal d-flex justify-content-center align-items-center w-100 h-100'
                    )}
                    onClick={closeModalCallback}>
                    <div
                        className="w-100"
                        onClick={stopClickEventPropagationFromCarousel}>
                        <BasicCarousel
                            ref={carouselRef}
                            className="w-100"
                            settings={settings}
                            render={() =>
                                images.map((image, index) => (
                                    <div
                                        key={index}
                                        className={
                                            'image-gallery-modal__image-gutter'
                                        }
                                        onClick={handleCloseModalClick}>
                                        <div
                                            className={`position-relative ${
                                                isVideoEnabled && image.video
                                                    ? 'w-100 h-100 d-flex'
                                                    : ''
                                            }`}
                                            onClick={stopClickEventsFromImage}>
                                            {isVideoEnabled && image.video ? (
                                                <ViewItemVideo
                                                    key={index}
                                                    className="py-7"
                                                    controls={true}
                                                    muted={false}
                                                    image={image}
                                                    isInView={
                                                        index ===
                                                        activeSlideIndex
                                                    }
                                                />
                                            ) : (
                                                <img
                                                    src={
                                                        image.normal ||
                                                        '//static.tradera.net/internal/site/auction/noImage_344x258.gif'
                                                    }
                                                    className="mx-auto d-block mw-100"
                                                    alt={image.alt}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))
                            }
                        />
                        <Button
                            title={t('vip_image_gallery_modal_close')}
                            size="lg"
                            theme="translucent"
                            ref={closeButtonRef}
                            round
                            className="image-gallery-close"
                            onClick={handleCloseModalClick}>
                            <CloseIcon className="icon icon-md" />
                        </Button>
                    </div>
                </DialogOverlay>
            </DialogPortal>
        </Dialog>
    );
};

ImageGalleryModal.propTypes = {
    images: PropTypes.array.isRequired,
    initialSlideIndex: PropTypes.number
};
ImageGalleryModal.defaultProps = {
    initialSlideIndex: 0
};

export { ImageGalleryModal };
